import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalProps = {
  title: string;
  description?: string;
  onRequestClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number;
  /** Disables Escape while a nested confirmation is open. */
  suspendEscape?: boolean;
  /** Rendered above the dialog (e.g. a confirmation). */
  overlay?: React.ReactNode;
};
export function Modal({
  title,
  description,
  onRequestClose,
  children,
  footer,
  width = 380,
  suspendEscape = false,
  overlay
}: ModalProps) {
  const titleId = useId();
  useEffect(() => {
    if (suspendEscape) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onRequestClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onRequestClose, suspendEscape]);
  return createPortal(<motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1b2129]/30 p-6" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.15
  }} onMouseDown={e => e.target === e.currentTarget && onRequestClose()} data-id="element-854" data-mp="2f8oq2fkqq4dg">
      
      <motion.div role="dialog" aria-modal="true" aria-labelledby={titleId} style={{
      width
    }} className="flex max-h-full flex-col rounded-[10px] bg-white font-sans text-pf-ink shadow-[0_12px_40px_rgba(20,28,38,0.25)]" initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.96
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-855" data-mp="2d4cmw5567q6h">
        
        <header className="flex items-start gap-3 border-b border-pf-line px-4 pb-[10px] pt-[12px]" data-id="element-856" data-mp="1ueeiq04huh84">
          <div className="min-w-0 flex-1" data-id="element-857" data-mp="1gggfnqvmi77x">
            <h2 id={titleId} className="text-[15px] font-bold text-pf-ink" data-id="element-858" data-mp="1kggrr4v926bt">
              {title}
            </h2>
            {description && <p className="mt-[2px] text-[11.5px] leading-snug text-pf-muted" data-id="element-859" data-mp="3n3dygz7nw96k">{description}</p>}
          </div>
          <button type="button" aria-label="Close" onClick={onRequestClose} className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-[5px] text-pf-ink transition-colors duration-150 hover:bg-pf-panel" data-id="element-860" data-mp="3iab2kkfewnn7">
            
            <XIcon className="h-[15px] w-[15px]" strokeWidth={2.6} data-id="element-861" data-mp="9uriaawpyfka" />
          </button>
        </header>
        <motion.div layoutScroll className="min-h-0 flex-1 overflow-y-auto px-4 py-[12px]" data-id="element-862" data-mp="4xivty4jfpd8">
          {children}
        </motion.div>
        {footer && <footer className="flex items-center gap-2 rounded-b-[10px] border-t border-pf-line bg-[#f8f8f9] px-4 py-[10px]" data-id="element-863" data-mp="3s1panglfivry">{footer}</footer>}
      </motion.div>
      {overlay}
    </motion.div>, document.body);
}